<template>
    <main v-if="fatalError">
        <Message severity="error">{{fatalError}}</Message>
    </main>
    <main v-if="!fatalError && !loaded" class="spinner-main">
        <ProgressSpinner></ProgressSpinner>
    </main>
    <main v-if="loaded" class="app-main">
        <component :is="currentModeComponent" @close="closeDialog"></component>
    </main>
</template>

<script>
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import { defineAsyncComponent, computed } from 'vue';
import { mapStores, mapState, mapActions } from 'pinia';
import { useDataStore } from './stores/data';
import { sendMessage } from './message-sender';
import setupMessageReceiver from './message-receiver';

const modes = {
    subscriptions: {
        componentName: 'SubscriptionsMode',
        load: () => import('./components/modes/SubscriptionsMode.vue'),
    },
    'payment-methods': {
        componentName: 'PaymentMethodsMode',
        load: () => import('./components/modes/PaymentMethodsMode.vue'),
    },
    'update-sub-pm': {
        componentName: 'UpdateSubscriptionPaymentMethodDialogMode',
        load: () => import('./components/modes/UpdateSubscriptionPaymentMethodDialogMode.vue'),
    },
    'update-sub-shipping-address': {
        componentName: 'UpdateSubscriptionShippingAddressDialogMode',
        load: () => import('./components/modes/UpdateSubscriptionShippingAddressDialogMode.vue'),
    },
    'add-payment-method': {
        componentName: 'AddPaymentMethodDialogMode',
        load: () => import('./components/modes/AddPaymentMethodDialogMode.vue'),
    },
    'update-payment-method': {
        componentName: 'UpdatePaymentMethodDialogMode',
        load: () => import('./components/modes/UpdatePaymentMethodDialogMode.vue'),
    },
    'remove-payment-method': {
        componentName: 'ConfirmRemovePaymentMethodDialogMode',
        load: () => import('./components/modes/ConfirmRemovePaymentMethodDialogMode.vue'),
    },
    'add-product': {
        componentName: 'AddProductDialogMode',
        load: () => import('./components/modes/AddProductDialogMode.vue'),
    },
    'skip-shipment': {
        componentName: 'SkipShipmentDialogMode',
        load: () => import('./components/modes/SkipShipmentDialogMode.vue'),
    },
    'cancel-subscription': {
        componentName: 'ConfirmCancelSubscriptionDialogMode',
        load: () => import('./components/modes/ConfirmCancelSubscriptionDialogMode.vue'),
    },
    'error-message': {
        componentName: 'ErrorMessageDialogMode',
        load: () => import('./components/modes/ErrorMessageDialogMode.vue'),
    },
    'subscription-dialog': {
        componentName: 'SubscriptionDialogMode',
        load: () => import('./components/modes/SubscriptionDialogMode.vue'),
    },
    'confirm-ship-now': {
        componentName: 'ConfirmShipNowDialogMode',
        load: () => import('./components/modes/ConfirmShipNowDialogMode.vue'),
    },
    'subscriptions-admin': {
        componentName: 'SubscriptionsAdminMode',
        load: () => import('./components/modes/SubscriptionsAdminMode.vue'),
    },
    'add-cs-note': {
        componentName: 'AddCsNoteDialogMode',
        load: () => import('./components/modes/admin/AddCsNoteDialogMode.vue'),
    },
    'pdp-add-to-subscription-dialog': {
        componentName: 'PdpAddToSubscriptionDialogMode',
        load: () => import('./components/modes/PdpAddToSubscriptionDialogMode.vue'),
    },
    'prop65-warning-dialog': {
        componentName: 'Prop65WarningDialogMode',
        load: () => import('./components/modes/Prop65WarningDialogMode.vue'),
    },
    'confirm-close-dialog': {
        componentName: 'ConfirmCloseDialogMode',
        load: () => import('./components/modes/ConfirmCloseDialogMode.vue'),
    },
};

const components = {};
Object.values(modes).forEach(mode => {
    components[mode.componentName] = defineAsyncComponent(mode.load);
});

export default {
    components: {
        Message,
        ProgressSpinner,
        ...components,
    },
    data: function() {
        return {
            fatalError: null,
            loaded: false,
            mode: 'subscriptions',
        };
    },
    provide: function() {
        return {
            countries: computed(() => this.countries),
        };
    },
    computed: {
        ...mapStores(useDataStore),
        ...mapState(useDataStore, [ 'countries' ]),
        currentModeComponent: function() {
            return modes[this.mode].componentName;
        },
    },
    watch: {
    },
    mounted: function() {
        setupMessageReceiver(this.dataStore);

        var params = new URLSearchParams(window.location.search);
        var dataid = params.get('dataid');
        if (this.loadFromStorage(dataid)) {
            this.mode = this.resolveModeFromUrl();
            this.loaded = true;
            return;
        }

        var admin = params.get('admin');
        var accountId = null;
        if (admin == '1' || admin == 'true') {
            admin = true;
            accountId = params.get('accountId');
        } else {
            admin = false;
        }

        var mode = this.resolveModeFromUrl();

        var self = this;
        dfajax({
            handler: 'manage-minibc-subscriptions',
            action: 'get-subscriptions',
            crossOrigin: 'auto',
            method: 'POST',
            payload: {
                admin,
                accountId,
            },
            successCallback: function(data) {
                self.setInitialData(data);
                self.mode = mode;
                self.loaded = true;
            },
            errorCallback: function(result) {
                if (result?.data?.notLoggedIn) {
                    sendMessage({
                        DRF: {
                            subject: 'login-required',
                        },
                    });
                }

                let error = result && result.data && result.data.error;
                if (error === true) {
                    error = result.data.message;
                }
                if (typeof error != 'string' || !error) {
                    error = 'An unexpected error has occurred';
                }
                self.fatalError = error;
            },
        });
    },
    methods: {
        resolveModeFromUrl: function() {
            var params = new URLSearchParams(window.location.search);
            var mode = params.get('mode');
            var modeDef = modes[mode];
            if (!modeDef) {
                mode = 'subscriptions';
                modeDef = modes[mode];
            }

            // Pre-load the mode component while we fetch data, so that it's ready to go when we need to display it
            modeDef.load();

            return mode;
        },
        closeDialog: function(data) {
            var dataid = data?.dataid;
            var reloadRequired = data?.reloadRequired;
            sendMessage({
                DRF: {
                    subject: 'close-dialog',
                    updateData: !!dataid,
                    dataid: dataid,
                    reloadRequired: !!reloadRequired,
                    alsoIframeId: data?.alsoIframeId,
                },
            });
        },
        ...mapActions(useDataStore, [ 'setInitialData', 'updateData', 'loadFromStorage' ]),
    }
};
</script>

<style lang="scss">
    @import "./common-tools.scss";

    main.spinner-main {
        display: flex;
        flex-direction: row;
        justify-content: center;
    }

    main > .mode-div > .page-message {
        display: block;
        font-size: remCalc(20px);
        @include use-body-font;
        color: var(--drf-text-color);
        font-weight: var(--drf-body2-font-weight);
        padding-bottom: $commonSpacing;

        a {
            @include shop-link;
        }
    }

    // Prime View hard-codes outline buttons as having a transparent background
    body .p-button-outlined {
        background: white;
    }
</style>
